/* global console, Office, window */

import { AuthMessage } from '../../auth/auth.types';
import { traceTokenStore } from '../../token/trace-token-store';
import { traceUserInfoStore } from '../../user-info/trace-user-info-store';
import { taskPaneUiSession } from '../session';
import { buildAuthProviderInfoUrl, SKIP_OFFICE_DIALOG_ALLOW_PROMPT } from '../taskpane.constants';
import { logAuthDebug } from '../utils/auth-debug.util';
import {
    clearOrgCodeFieldValidation,
    focusOrgCodeInput,
    getTrimmedOrgCodeInputValue,
    persistLastOrgCode,
    readPersistedOrgCode,
    setOrgCodeInputValue,
    showOrgCodeFieldValidation,
} from '../utils/org-code-field.util';
import { getOrgCodeValidationMessage } from '../utils/validate-org-code.util';

export interface TaskPaneOfficeAuthFlowDeps {
    /** Host: refresh shell UI when auth/session state that affects it changes. */
    syncUi: () => void;
    /** Host: run after the auth dialog has persisted new tokens successfully. */
    afterSuccessfulLogin: () => void;
}

/** Connect / switch-organisation UX and Office `displayDialogAsync` sign-in. */
export class TaskPaneOfficeAuthFlow {
    constructor(private readonly deps: TaskPaneOfficeAuthFlowDeps) {}

    clearAuthStatusDetail(): void {
        const detail = document.getElementById('auth-status-detail');
        if (detail) {
            detail.textContent = '';
            detail.classList.add('hidden');
        }
    }

    /** Clears tokens and user-info cache (local sign-out). */
    clearLocalAuthSession(): void {
        traceTokenStore.clearTokenData();
        traceUserInfoStore.clear();
    }

    /**
     * Not connected: validate org and open IdP dialog.
     * Connected (compact): enter switch-organisation flow (keep session).
     * Connected (switch flow): clear session, then open IdP with the org field value.
     */
    handleConnectButtonClick(): void {
        if (traceTokenStore.hasConnectedSession()) {
            if (!taskPaneUiSession.isSwitchOrganisationFlowActive()) {
                taskPaneUiSession.setSwitchOrganisationFlowActive(true);
                const stored = readPersistedOrgCode();
                if (stored) {
                    setOrgCodeInputValue(stored);
                }
                clearOrgCodeFieldValidation();
                this.clearAuthStatusDetail();
                this.deps.syncUi();
                focusOrgCodeInput();
                return;
            }
            this.clearLocalAuthSession();
            this.clearAuthStatusDetail();
            this.deps.syncUi();
        }
        this.openAuthDialog();
    }

    handleSwitchOrganisationCancel(): void {
        taskPaneUiSession.setSwitchOrganisationFlowActive(false);
        clearOrgCodeFieldValidation();
        this.clearAuthStatusDetail();
        this.deps.syncUi();
    }

    /**
     * Opens the IdP flow in an Office dialog. `provider=2` routes to Keycloak; redirect must land on `auth/callback.html`.
     */
    openAuthDialog(): void {
        const orgCode = getTrimmedOrgCodeInputValue();
        const orgMessage = getOrgCodeValidationMessage(orgCode);
        if (orgMessage !== null) {
            showOrgCodeFieldValidation(orgMessage);
            focusOrgCodeInput();
            return;
        }

        clearOrgCodeFieldValidation();

        taskPaneUiSession.setAuthDialogMessageHandled(false);

        Office.context.ui.displayDialogAsync(
            buildAuthProviderInfoUrl(orgCode),
            {
                height: 60,
                width: 30,
                displayInIframe: false,
                ...(SKIP_OFFICE_DIALOG_ALLOW_PROMPT ? { promptBeforeOpen: false } : {}),
            },
            (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    const err = asyncResult.error as { code?: number; message?: string } | undefined;
                    const code = err?.code;
                    console.error('displayDialogAsync failed', code, err?.message);

                    if (code === 12009 || code === 12011) {
                        this.setAuthHint(
                            'Sign-in window was blocked. In your browser, allow pop-ups for Excel / Microsoft 365 for this site, then try Connect again.',
                        );
                    } else if (err?.message) {
                        this.setAuthHint(`Could not open sign-in: ${err.message}`);
                    } else {
                        this.setAuthHint('Could not open sign-in window. Try again.');
                    }
                    return;
                }

                const loginDialog = asyncResult.value;

                loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
                    const raw =
                        typeof arg === 'object' &&
                        arg !== null &&
                        'message' in arg &&
                        typeof arg.message === 'string'
                            ? arg.message.trim()
                            : '';

                    if (!raw) {
                        console.error('DialogMessageReceived: missing or empty message.', arg);
                        taskPaneUiSession.setAuthDialogMessageHandled(true);
                        this.setAuthHint(
                            'Sign-in did not return data to Excel. Try again or contact support.',
                        );
                        try {
                            loginDialog.close();
                        } catch {
                            /* ignore */
                        }
                        return;
                    }

                    let authData: AuthMessage;

                    try {
                        authData = JSON.parse(raw) as AuthMessage;
                    } catch (error) {
                        console.error('Invalid auth callback payload', error);
                        taskPaneUiSession.setAuthDialogMessageHandled(true);
                        this.setAuthHint('Login failed: invalid callback response.');
                        try {
                            loginDialog.close();
                        } catch {
                            /* ignore */
                        }
                        return;
                    }

                    if (
                        authData.status === 'success' &&
                        authData.tokenData?.accessToken &&
                        authData.tokenData.refreshToken
                    ) {
                        taskPaneUiSession.setAuthDialogMessageHandled(true);
                        logAuthDebug('DialogMessageReceived: success (tokens stored, lengths only)', {
                            accessTokenChars: authData.tokenData.accessToken.length,
                            refreshTokenChars: authData.tokenData.refreshToken.length,
                        });
                        const orgForStore = getTrimmedOrgCodeInputValue();
                        if (orgForStore) {
                            persistLastOrgCode(orgForStore);
                        }
                        traceTokenStore.setTokenData(authData.tokenData);
                        try {
                            loginDialog.close();
                        } catch {
                            /* ignore */
                        }
                        window.setTimeout(() => {
                            this.deps.afterSuccessfulLogin();
                        }, 0);
                    } else {
                        logAuthDebug('DialogMessageReceived: non-success or incomplete token pair', {
                            status: authData.status,
                            message: authData.message,
                            hasTokenData: Boolean(authData.tokenData),
                            hasRefreshToken: Boolean(authData.tokenData?.refreshToken),
                        });
                        console.error('Auth Error:', authData.message);
                        taskPaneUiSession.setAuthDialogMessageHandled(true);
                        this.setAuthHint(`Login failed: ${authData.message ?? 'Unknown error'}`);
                        try {
                            loginDialog.close();
                        } catch {
                            /* ignore */
                        }
                    }
                });

                loginDialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
                    if (taskPaneUiSession.isAuthDialogMessageHandled()) {
                        return;
                    }
                    const code =
                        typeof arg === 'object' && arg !== null && 'error' in arg
                            ? (arg.error as number | undefined)
                            : undefined;
                    console.warn('Auth dialog event before tokens were delivered.', code, arg);

                    if (code === 12002) {
                        this.setAuthHint(
                            'Sign-in page could not be loaded in the popup. Check the network URL or try again.',
                        );
                        return;
                    }

                    if (code === 12006) {
                        this.setAuthHint(
                            'Sign-in closed before connecting. The add-in did not receive tokens — ensure login redirects to this add-in’s auth callback with the OTP in the URL hash.',
                        );
                    }
                });
            },
        );
    }

    /** Disconnected auth strip plus visible detail message (errors, dialog hints). */
    setAuthHint(detailMessage: string): void {
        const authStatus = document.getElementById('auth-status');
        const detail = document.getElementById('auth-status-detail');
        const connectedDot = document.getElementById('auth-connected-dot');
        if (!authStatus) {
            return;
        }
        connectedDot?.classList.add('hidden');
        authStatus.textContent = 'Not connected';
        authStatus.classList.remove('hint--success', 'hint--error');

        if (detail) {
            detail.textContent = detailMessage;
            detail.classList.remove('hidden');
        }
    }
}
